import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Heart, Stethoscope, ArrowRight, ArrowLeft, Check, AlertCircle,
  Award, Users, Camera, CheckCircle, Sparkles, Instagram, Youtube, Linkedin,
  Twitter, ExternalLink, Shield
} from 'lucide-react'
import { useAuth } from '../../App'

const STEPS = [
  { id: 'credentials', title: 'Credentials', icon: Award },
  { id: 'specialties', title: 'Specialties', icon: Stethoscope },
  { id: 'platforms', title: 'Platforms', icon: Camera },
  { id: 'complete', title: 'Complete', icon: CheckCircle },
]

const PROFESSIONAL_TYPES = [
  { value: 'physician', label: 'Physician (MD/DO)', description: 'Licensed medical doctor' },
  { value: 'nurse', label: 'Nurse (RN/NP/APRN)', description: 'Registered or advanced practice nurse' },
  { value: 'pharmacist', label: 'Pharmacist (PharmD)', description: 'Licensed pharmacist' },
  { value: 'dentist', label: 'Dentist (DDS/DMD)', description: 'Licensed dental professional' },
  { value: 'therapist', label: 'Therapist (PT/OT/SLP)', description: 'Physical, occupational, or speech therapist' },
  { value: 'mental-health', label: 'Mental Health Professional', description: 'Psychologist, counselor, social worker' },
  { value: 'patient-advocate', label: 'Patient Advocate', description: 'Living with or caring for a health condition' },
  { value: 'health-educator', label: 'Health Educator', description: 'Certified health education specialist' },
  { value: 'researcher', label: 'Researcher/Scientist', description: 'PhD or research professional' },
  { value: 'fitness-wellness', label: 'Fitness & Wellness Coach', description: 'Certified trainer or wellness coach' },
]

const SPECIALTIES = [
  'Cardiology', 'Dermatology', 'Endocrinology', 'Gastroenterology',
  'General Practice / Family Medicine', 'Geriatrics', 'Hematology',
  'Infectious Disease', 'Nephrology', 'Neurology', 'Neurosurgery',
  'Obstetrics & Gynecology', 'Oncology', 'Ophthalmology', 'Orthopedics',
  'Pediatrics', 'Plastic Surgery', 'Psychiatry', 'Pulmonology',
  'Radiology', 'Rheumatology', 'Sports Medicine', 'Urology',
  'Mental Health', 'Nutrition & Dietetics', 'Women\'s Health',
  'Chronic Disease Management', 'Patient Advocacy', 'Health & Wellness',
]

const PLATFORMS = [
  { id: 'instagram', name: 'Instagram', icon: Instagram, color: 'pink' },
  { id: 'tiktok', name: 'TikTok', icon: () => <span className="text-lg font-bold">T</span>, color: 'gray' },
  { id: 'youtube', name: 'YouTube', icon: Youtube, color: 'red' },
  { id: 'linkedin', name: 'LinkedIn', icon: Linkedin, color: 'blue' },
  { id: 'twitter', name: 'Twitter/X', icon: Twitter, color: 'sky' },
]

function CreatorOnboarding() {
  const navigate = useNavigate()
  const { user, updateUserProfile } = useAuth()
  const [currentStep, setCurrentStep] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')

  const [formData, setFormData] = useState({
    professionalType: '',
    title: '',
    licenseState: '',
    npiNumber: '',
    specialties: [],
    patientCondition: '',
    platforms: {},
    primaryPlatform: '',
    contentFocus: '',
  })

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    setError('')
  }

  const handleSpecialtyToggle = (specialty) => {
    setFormData(prev => {
      const current = prev.specialties
      if (current.includes(specialty)) {
        return { ...prev, specialties: current.filter(s => s !== specialty) }
      }
      if (current.length >= 5) {
        return prev // Max 5 specialties
      }
      return { ...prev, specialties: [...current, specialty] }
    })
    setError('')
  }

  const handlePlatformChange = (platformId, field, value) => {
    setFormData(prev => ({
      ...prev,
      platforms: {
        ...prev.platforms,
        [platformId]: {
          ...prev.platforms[platformId],
          [field]: value,
        },
      },
    }))
    setError('')
  }

  const togglePlatform = (platformId) => {
    setFormData(prev => {
      const platforms = { ...prev.platforms }
      if (platforms[platformId]) {
        delete platforms[platformId]
        // If this was the primary platform, reset it
        const newPrimary = prev.primaryPlatform === platformId ? '' : prev.primaryPlatform
        return { ...prev, platforms, primaryPlatform: newPrimary }
      }
      platforms[platformId] = { handle: '', followers: '' }
      return { ...prev, platforms }
    })
  }

  const validateStep = () => {
    switch (currentStep) {
      case 0: // Credentials
        if (!formData.professionalType) {
          setError('Please select your professional type')
          return false
        }
        if (!formData.title.trim()) {
          setError('Please enter your professional title')
          return false
        }
        break
      case 1: // Specialties
        if (formData.specialties.length === 0) {
          setError('Please select at least one specialty')
          return false
        }
        break
      case 2: // Platforms
        if (Object.keys(formData.platforms).length === 0) {
          setError('Please add at least one social platform')
          return false
        }
        if (!formData.primaryPlatform) {
          setError('Please select your primary platform')
          return false
        }
        // Validate that each platform has a handle
        for (const [platformId, data] of Object.entries(formData.platforms)) {
          if (!data.handle?.trim()) {
            setError('Please enter your handle for all selected platforms')
            return false
          }
        }
        break
    }
    return true
  }

  const handleNext = () => {
    if (!validateStep()) return

    if (currentStep < STEPS.length - 1) {
      setCurrentStep(prev => prev + 1)
    }
  }

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1)
    }
  }

  const handleComplete = async () => {
    setIsLoading(true)
    setError('')

    try {
      const response = await fetch('/api/onboarding', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.id,
          profile: formData,
          markComplete: true,
        }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Failed to complete onboarding')
      }

      updateUserProfile(data.user)
      navigate('/creator')
    } catch (err) {
      setError(err.message || 'Failed to complete onboarding. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const renderCredentialsStep = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900">Your professional background</h2>
        <p className="mt-2 text-gray-600">Help us verify your healthcare expertise</p>
      </div>

      <div>
        <label className="label">What best describes you?</label>
        <div className="grid gap-3 max-h-[300px] overflow-y-auto pr-2">
          {PROFESSIONAL_TYPES.map(type => (
            <button
              key={type.value}
              type="button"
              onClick={() => handleInputChange({ target: { name: 'professionalType', value: type.value } })}
              className={`flex items-center p-4 rounded-xl border-2 transition-all text-left ${
                formData.professionalType === type.value
                  ? 'border-teal-500 bg-teal-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mr-4 flex-shrink-0 ${
                formData.professionalType === type.value
                  ? 'border-teal-500 bg-teal-500'
                  : 'border-gray-300'
              }`}>
                {formData.professionalType === type.value && (
                  <Check className="w-3 h-3 text-white" />
                )}
              </div>
              <div>
                <p className="font-medium text-gray-900">{type.label}</p>
                <p className="text-sm text-gray-500">{type.description}</p>
              </div>
            </button>
          ))}
        </div>
      </div>

      <div>
        <label htmlFor="title" className="label">Your professional title</label>
        <input
          id="title"
          name="title"
          type="text"
          value={formData.title}
          onChange={handleInputChange}
          className="input"
          placeholder="e.g., Board-Certified Cardiologist, ICU Nurse, Health Coach"
        />
        <p className="mt-1 text-xs text-gray-500">This will be displayed on your creator profile</p>
      </div>

      {['physician', 'nurse', 'pharmacist', 'dentist', 'therapist', 'mental-health'].includes(formData.professionalType) && (
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="licenseState" className="label">License state (optional)</label>
            <input
              id="licenseState"
              name="licenseState"
              type="text"
              value={formData.licenseState}
              onChange={handleInputChange}
              className="input"
              placeholder="e.g., California"
            />
          </div>
          <div>
            <label htmlFor="npiNumber" className="label">NPI number (optional)</label>
            <input
              id="npiNumber"
              name="npiNumber"
              type="text"
              value={formData.npiNumber}
              onChange={handleInputChange}
              className="input"
              placeholder="10-digit NPI"
            />
          </div>
        </div>
      )}

      {formData.professionalType === 'patient-advocate' && (
        <div>
          <label htmlFor="patientCondition" className="label">What condition do you advocate for?</label>
          <input
            id="patientCondition"
            name="patientCondition"
            type="text"
            value={formData.patientCondition}
            onChange={handleInputChange}
            className="input"
            placeholder="e.g., Type 1 Diabetes, Multiple Sclerosis, Cancer Survivor"
          />
        </div>
      )}

      <div className="bg-teal-50 rounded-xl p-4 flex items-start">
        <Shield className="w-5 h-5 text-teal-600 mr-3 mt-0.5 flex-shrink-0" />
        <div className="text-sm text-teal-800">
          <p className="font-medium">Verification process</p>
          <p className="mt-1 text-teal-700">
            After onboarding, our team may request documentation to verify your credentials.
            Verified creators receive a "SOFI Verified" badge and priority matching.
          </p>
        </div>
      </div>
    </div>
  )

  const renderSpecialtiesStep = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900">Your areas of expertise</h2>
        <p className="mt-2 text-gray-600">Select up to 5 specialties that match your content</p>
      </div>

      <div className="flex flex-wrap gap-2 max-h-[400px] overflow-y-auto p-1">
        {SPECIALTIES.map(specialty => (
          <button
            key={specialty}
            type="button"
            onClick={() => handleSpecialtyToggle(specialty)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
              formData.specialties.includes(specialty)
                ? 'bg-teal-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {formData.specialties.includes(specialty) && (
              <Check className="w-4 h-4 inline mr-1" />
            )}
            {specialty}
          </button>
        ))}
      </div>

      <div className="text-sm text-gray-500 text-center">
        {formData.specialties.length}/5 selected
      </div>

      <div>
        <label htmlFor="contentFocus" className="label">Describe your content focus (optional)</label>
        <textarea
          id="contentFocus"
          name="contentFocus"
          value={formData.contentFocus}
          onChange={handleInputChange}
          className="input min-h-[100px]"
          placeholder="e.g., I create educational content about heart health for patients, focusing on lifestyle changes and prevention..."
        />
      </div>
    </div>
  )

  const renderPlatformsStep = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900">Your social presence</h2>
        <p className="mt-2 text-gray-600">Connect your platforms to showcase your reach</p>
      </div>

      <div className="space-y-4">
        {PLATFORMS.map(platform => {
          const isActive = !!formData.platforms[platform.id]
          const IconComponent = platform.icon

          return (
            <div key={platform.id} className="border rounded-xl overflow-hidden">
              <button
                type="button"
                onClick={() => togglePlatform(platform.id)}
                className={`w-full flex items-center p-4 transition-colors ${
                  isActive ? 'bg-gray-50' : 'hover:bg-gray-50'
                }`}
              >
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  isActive ? `bg-${platform.color}-100` : 'bg-gray-100'
                }`}>
                  <IconComponent className={`w-5 h-5 ${isActive ? `text-${platform.color}-600` : 'text-gray-400'}`} />
                </div>
                <span className="ml-3 font-medium text-gray-900">{platform.name}</span>
                <div className={`ml-auto w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                  isActive ? 'border-teal-500 bg-teal-500' : 'border-gray-300'
                }`}>
                  {isActive && <Check className="w-4 h-4 text-white" />}
                </div>
              </button>

              {isActive && (
                <div className="px-4 pb-4 pt-2 border-t bg-gray-50 grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="text-xs font-medium text-gray-600">Handle / Username</label>
                    <input
                      type="text"
                      value={formData.platforms[platform.id]?.handle || ''}
                      onChange={(e) => handlePlatformChange(platform.id, 'handle', e.target.value)}
                      className="input mt-1"
                      placeholder="@yourusername"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600">Follower Count</label>
                    <select
                      value={formData.platforms[platform.id]?.followers || ''}
                      onChange={(e) => handlePlatformChange(platform.id, 'followers', e.target.value)}
                      className="input mt-1"
                    >
                      <option value="">Select range</option>
                      <option value="under-10k">Under 10K</option>
                      <option value="10k-50k">10K - 50K</option>
                      <option value="50k-100k">50K - 100K</option>
                      <option value="100k-500k">100K - 500K</option>
                      <option value="500k-1m">500K - 1M</option>
                      <option value="over-1m">Over 1M</option>
                    </select>
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>

      {Object.keys(formData.platforms).length > 0 && (
        <div>
          <label className="label">Primary platform</label>
          <p className="text-sm text-gray-500 mb-2">Where do you create most of your content?</p>
          <div className="flex flex-wrap gap-2">
            {Object.keys(formData.platforms).map(platformId => {
              const platform = PLATFORMS.find(p => p.id === platformId)
              return (
                <button
                  key={platformId}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, primaryPlatform: platformId }))}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    formData.primaryPlatform === platformId
                      ? 'bg-teal-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {platform?.name}
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )

  const renderCompleteStep = () => (
    <div className="text-center space-y-6">
      <div className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center">
        <CheckCircle className="w-10 h-10 text-green-600" />
      </div>

      <div>
        <h2 className="text-2xl font-bold text-gray-900">Welcome to SOFI Health!</h2>
        <p className="mt-2 text-gray-600">
          Your creator profile is ready. Let's start connecting you with amazing brand opportunities.
        </p>
      </div>

      <div className="bg-teal-50 rounded-xl p-6 text-left">
        <h3 className="font-semibold text-gray-900 mb-4">What's next?</h3>
        <ul className="space-y-3">
          <li className="flex items-start">
            <div className="w-6 h-6 bg-teal-100 rounded-full flex items-center justify-center mr-3 mt-0.5">
              <span className="text-teal-600 text-sm font-medium">1</span>
            </div>
            <div>
              <p className="font-medium text-gray-900">Complete your profile</p>
              <p className="text-sm text-gray-600">Add a photo and bio to stand out</p>
            </div>
          </li>
          <li className="flex items-start">
            <div className="w-6 h-6 bg-teal-100 rounded-full flex items-center justify-center mr-3 mt-0.5">
              <span className="text-teal-600 text-sm font-medium">2</span>
            </div>
            <div>
              <p className="font-medium text-gray-900">Browse opportunities</p>
              <p className="text-sm text-gray-600">See campaigns matched to your expertise</p>
            </div>
          </li>
          <li className="flex items-start">
            <div className="w-6 h-6 bg-teal-100 rounded-full flex items-center justify-center mr-3 mt-0.5">
              <span className="text-teal-600 text-sm font-medium">3</span>
            </div>
            <div>
              <p className="font-medium text-gray-900">Get verified</p>
              <p className="text-sm text-gray-600">Submit credentials for priority matching</p>
            </div>
          </li>
        </ul>
      </div>

      <button
        onClick={handleComplete}
        disabled={isLoading}
        className="w-full py-3 rounded-lg font-medium flex items-center justify-center bg-teal-600 hover:bg-teal-700 text-white transition-colors"
      >
        {isLoading ? 'Setting up your account...' : 'Go to Dashboard'}
        {!isLoading && <ArrowRight className="ml-2 w-4 h-4" />}
      </button>
    </div>
  )

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-4 py-4">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className="w-10 h-10 bg-gradient-to-br from-teal-600 to-teal-700 rounded-xl flex items-center justify-center">
              <Heart className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold text-gray-900">SOFI Health</span>
          </div>
          <div className="flex items-center text-sm text-gray-500">
            <Sparkles className="w-4 h-4 mr-1 text-yellow-500" />
            Setting up your creator profile
          </div>
        </div>
      </header>

      {/* Progress Bar */}
      <div className="bg-white border-b border-gray-200 px-4 py-4">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center justify-between">
            {STEPS.map((step, index) => (
              <React.Fragment key={step.id}>
                <div className="flex flex-col items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    index < currentStep
                      ? 'bg-teal-600 text-white'
                      : index === currentStep
                        ? 'bg-teal-100 text-teal-600 border-2 border-teal-600'
                        : 'bg-gray-100 text-gray-400'
                  }`}>
                    {index < currentStep ? (
                      <Check className="w-5 h-5" />
                    ) : (
                      <step.icon className="w-5 h-5" />
                    )}
                  </div>
                  <span className={`mt-2 text-xs font-medium ${
                    index <= currentStep ? 'text-teal-600' : 'text-gray-400'
                  }`}>
                    {step.title}
                  </span>
                </div>
                {index < STEPS.length - 1 && (
                  <div className={`flex-1 h-1 mx-2 rounded ${
                    index < currentStep ? 'bg-teal-600' : 'bg-gray-200'
                  }`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-1 px-4 py-8">
        <div className="max-w-xl mx-auto">
          {error && (
            <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-start">
              <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
              {error}
            </div>
          )}

          {currentStep === 0 && renderCredentialsStep()}
          {currentStep === 1 && renderSpecialtiesStep()}
          {currentStep === 2 && renderPlatformsStep()}
          {currentStep === 3 && renderCompleteStep()}

          {/* Navigation Buttons */}
          {currentStep < 3 && (
            <div className="mt-8 flex justify-between">
              <button
                type="button"
                onClick={handleBack}
                disabled={currentStep === 0}
                className={`flex items-center px-4 py-2 rounded-lg font-medium transition-colors ${
                  currentStep === 0
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </button>
              <button
                type="button"
                onClick={handleNext}
                className="flex items-center px-6 py-2 rounded-lg font-medium bg-teal-600 hover:bg-teal-700 text-white transition-colors"
              >
                Continue
                <ArrowRight className="ml-2 w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </main>
    </div>
  )
}

export default CreatorOnboarding
